import { CreatorStatusFilter, PlatformFilter } from '@/components/filters';
import { FilterGroup } from '@/components/filters/filter-button';
import { FilterPanel } from '@/components/ui';
import { SearchInput } from '@/components/ui/search-input';
import { Button, HStack } from '@chakra-ui/react';
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Xmark } from 'iconoir-react';

const creatorQueryParsers = {
  creatorSearch: parseAsString.withDefault(''),
  creatorPlatform: parseAsString,
  creatorStatus: parseAsString,
  creatorPage: parseAsInteger.withDefault(1),
};

const Filters = () => {
  const { t } = useTranslation();
  // URL state management with nuqs - using namespaced parameters
  const [
    { creatorSearch: search, creatorPlatform: platform, creatorStatus: status },
    setCreatorQuery,
  ] = useQueryStates(creatorQueryParsers);

  const handleSearchChange = useCallback(
    (newSearch: string) => {
      setCreatorQuery({ creatorSearch: newSearch, creatorPage: 1 });
    },
    [setCreatorQuery]
  );

  const handlePlatformChange = useCallback(
    (newPlatform: string | null) => {
      setCreatorQuery({ creatorPlatform: newPlatform, creatorPage: 1 });
    },
    [setCreatorQuery]
  );

  const handleStatusChange = useCallback(
    (newStatus: string | null) => {
      setCreatorQuery({ creatorStatus: newStatus, creatorPage: 1 });
    },
    [setCreatorQuery]
  );

  const handleClearAll = useCallback(() => {
    setCreatorQuery({
      creatorSearch: '',
      creatorPlatform: null,
      creatorStatus: null,
      creatorPage: 1,
    });
  }, [setCreatorQuery]);

  const hasActiveFilters = useMemo(
    () => search !== '' || !!platform || !!status,
    [search, platform, status]
  );

  return (
    <FilterPanel
      w="full"
      actionsSlot={
        <FilterGroup>
          <HStack gap={2} alignItems="center" flexShrink={0} wrap="wrap" justifyContent="flex-end">
            <PlatformFilter
              value={platform}
              onChange={handlePlatformChange}
              initialValue={platform}
            />
            <CreatorStatusFilter
              value={status}
              onChange={handleStatusChange}
              initialValue={status}
            />
            {hasActiveFilters && (
              <Button size="sm" variant="ghost" colorPalette="gray" onClick={handleClearAll}>
                <Xmark width={16} height={16} />
                {t('common.clearAll')}
              </Button>
            )}
          </HStack>
        </FilterGroup>
      }
    >
      <SearchInput
        value={search}
        onChange={handleSearchChange}
        placeholder={t('creators.filters.search')}
        maxWidth="300px"
        size="sm"
      />
    </FilterPanel>
  );
};

export default Filters;
